import {
  ACCESSORIES,
  ALL_ELECTRONICS_PRODUCT_TYPES,
  CASE,
  KIT,
  OPTICAL,
  SUN,
} from '@/scripts/constants/pdp'
import {
  ElectronicsFeature,
  Features,
  FrameFeature,
  LensesFeature,
  Product,
  Size,
} from '@/types/pdp'
import wcs from '@/scripts/libs/wcs'
import PDP from '@/scripts/libs/pdp'
import { isProductStellaByProductType } from '@/scripts/libs/products'

export interface ProductDetail {
  key: string
  icon: string
  features: {
    key: string
    feature: string
  }[]
  link?: {
    label: string
    href: string
  }
  sizePanel?: {
    label: string
  }
}

interface IncludedInYourOrder {
  icon: string
  text: string
}

class ProductDetails {
  private translation: (_key: string) => '' = () => ''

  private translationPrefix = 'pdp.product_details.'

  private translate(key: string): string {
    return this.translation(`${this.translationPrefix}${key}`)
  }

  mapDetails = (product: Product, translation: (_key: string) => '', isNoEcommerce: boolean) => {
    this.translation = translation

    const productIsSupernova = product.isSupernova
    const productDetailsFeatures = []

    if (product.type === KIT) {
      return []
    }

    if (product.type === CASE) {
      const caseFeatures = this.mapCaseFeatures(product.features.frame)
      return [caseFeatures]
    }

    if (product.features) {
      const features = product.features

      if (features.frame) {
        const frameDescription = this.mapFrameDescription(features?.frame, productIsSupernova)
        productDetailsFeatures.push(frameDescription)
      }

      if (features.lenses) {
        const lensInformation = this.mapLensInformation(features?.lenses, product, translation)
        productDetailsFeatures.push(lensInformation)
      }

      const size = product.sizes?.find((size) => size.catEntryId === product.catEntryId)

      const productDimentions = this.mapProductDimentions(features, size, productIsSupernova)
      productDetailsFeatures.push(productDimentions)

      if (
        product.type === SUN ||
        ALL_ELECTRONICS_PRODUCT_TYPES.includes(product.type) ||
        product.type === OPTICAL
      ) {
        const fitting = this.mapFittingDetail(product, translation, isNoEcommerce)
        productDetailsFeatures.push(fitting)
      }

      if (ALL_ELECTRONICS_PRODUCT_TYPES.includes(product.type)) {
        const electronicsFeatures = this.mapElectronicsFeatures(product)
        electronicsFeatures.forEach((feature) => productDetailsFeatures.push(feature))
      }
    }

    return productDetailsFeatures
  }

  mapElectronicsFeatures = (product: Product): ProductDetail[] => {
    const camera = this.mapCameraFeatures(product.features.electronics)
    const audio = this.mapAudioFeatures(product.features.electronics)
    const controls = this.mapControls(product.features.electronics, product.isSupernova)
    const batteryAndMemory = this.mapBatteryAndMemory(
      product.features.electronics,
      product.isSupernova
    )
    const connectivity = this.mapConnectivity(product.features.electronics, product.isSupernova)
    return [camera, audio, controls, batteryAndMemory, connectivity]
  }

  private getBatterySupNumber = (isSupernova: boolean) => {
    if (isSupernova) {
      return wcs.isUSA() ? 4 : 3
    }

    return 4
  }

  mapConnectivity = (
    electronicsFeatures: ElectronicsFeature,
    isSupernova: boolean
  ): ProductDetail => {
    const connectivity = {
      key: this.translate('connectivity'),
      icon: 'stella-wifi',
      features: [
        {
          key: this.translate('wifi'),
          feature: electronicsFeatures.connectivity?.wifi ?? '',
        },
        {
          key: this.translate('bluetooth'),
          feature: electronicsFeatures.connectivity?.bluetooth ?? '',
        },
        {
          key: this.translate('os_compatibility'),
          feature: electronicsFeatures.osCompatibility,
        },
      ],
    } as ProductDetail

    if (isSupernova) {
      connectivity.link = {
        label: this.translate('supported_phone'),
        href: 'https://media.ray-ban.com/scripts/smart-glasses/rbm_compatiblephones.pdf',
      }
    }

    return connectivity
  }

  mapBatteryAndMemory = (
    electronicsFeatures: ElectronicsFeature,
    isSupernova: boolean
  ): ProductDetail => {
    return {
      key: this.translate('battery_and_memory'),
      icon: 'stella-battery',
      features: [
        {
          key: this.translate('battery'),
          feature: `${electronicsFeatures.battery?.type} ${
            electronicsFeatures.battery?.duration
          }<sup>(${this.getBatterySupNumber(isSupernova)})</sup>`,
        },
        {
          key: this.translate('memory'),
          feature: `${electronicsFeatures.memory?.size} ${electronicsFeatures.memory?.capacity}`,
        },
      ],
    }
  }

  private getVoiceTranslation(isSupernova: boolean) {
    if (isSupernova) {
      return `${this.translate('voice')}<sub>(2)</sub>`
    }
    return this.translate('voice')
  }

  mapControls = (electronicsFeatures: ElectronicsFeature, isSupernova: boolean): ProductDetail => {
    const controls = {
      key: this.translate('controls'),
      icon: 'stella-controls',
      features: [
        {
          key: this.translate('touch'),
          feature: '',
        },
        {
          key: this.getVoiceTranslation(isSupernova),
          feature: '',
        },
      ],
    }

    if (!isSupernova || (isSupernova && wcs.isUSA())) {
      controls.features.push({
        key: `${this.translate('assistant_type')}`,
        feature: `${electronicsFeatures.assistantType}<sup>(3)</sup>`,
      })
    }

    return controls
  }

  mapAudioFeatures = (electronicsFeatures: ElectronicsFeature): ProductDetail => {
    return {
      key: this.translate('audio'),
      icon: 'stella-audio',
      features: [
        {
          key: this.translate('speaker'),
          feature: electronicsFeatures.audioSystem?.output ?? '',
        },
        {
          key: this.translate('microphone'),
          feature: electronicsFeatures.audioSystem?.input ?? '',
        },
      ],
    }
  }

  mapCameraFeatures = (electronicsFeatures: ElectronicsFeature): ProductDetail => {
    return {
      key: this.translate('camera'),
      icon: 'stella-camera',
      features: [
        {
          key: this.translate('image_acquisition'),
          feature: electronicsFeatures.acquisition.image,
        },
        {
          key: this.translate('video_acquisition'),
          feature: electronicsFeatures.acquisition.video,
        },
      ],
    }
  }

  mapCaseFeatures = (frame: FrameFeature): ProductDetail => {
    return {
      key: this.translate('features'),
      // TODO: adjust with correct icon
      icon: 'case-finish',
      features: [
        {
          key: this.translate('material'),
          feature: frame.materialPrimary,
        },
        {
          key: this.translate('color'),
          feature: frame.color,
        },
        {
          key: this.translate('finish'),
          feature: frame.materialSecondary,
        },
      ],
    }
  }

  mapFittingDetail = (
    product: Product,
    translation: (_key: string) => '',
    isNoEcommerce: boolean
  ): ProductDetail => {
    const sizeGuideUrl = wcs.getEndPoint('sizeGuidePage')
    const geoFitId = product.geofits?.find((element) => element.catEntryId === product.catEntryId)
    const productIsSupernova = product.isSupernova
    const fittingDetailIcon = productIsSupernova ? 'smart-glasses-on' : 'face-shape-fit'

    const fitting = PDP.sanitizeBridgeAndNosepadsTranslationKey(geoFitId?.key ?? '')
    const translatedFitting = translation(`pdp.bridge_and_nosepads.${fitting}`)
    const fittingDetail = {
      key: this.translate('fitting'),
      // TODO: adjust with correct icon
      icon: fittingDetailIcon,
      features: [
        {
          key: this.translate('fitting'),
          feature: product.frameFit,
        },
        {
          key: this.translate('bridge_nosepads'),
          feature: translatedFitting,
        },
      ],
    } as ProductDetail

    return fittingDetail
  }

  mapFrameDescription = (frame: FrameFeature, productIsSupernova: boolean): ProductDetail => {
    const frameDescriptionIcon = productIsSupernova
      ? 'smart-glasses-frames'
      : 'sunglasses-frame-color'
    const frameDescription = {
      key: this.translate('frame_description'),
      icon: frameDescriptionIcon,
      features: [
        {
          key: this.translate('frame_shape'),
          feature: frame.shape,
        },
        {
          key: this.translate('frame_color'),
          feature: frame.color,
        },
        {
          key: this.translate('frame_material'),
          feature: frame.materialPrimary,
        },
      ],
    }

    if (frame.templeColor) {
      frameDescription.features.push({
        key: this.translate('temple_color'),
        feature: frame.templeColor,
      })
    }

    return frameDescription
  }

  mapLensInformation = (
    lenses: LensesFeature,
    product: Product,
    translation: (_key: string) => ''
  ): ProductDetail => {
    const drivingMessage = translation('pdp.product_details.suitable_for_driving')
    const lensInformation: ProductDetail = {
      key: this.translate('lens_information'),
      icon: 'sunglasses-lens-color',
      features: [
        {
          key: this.translate('lens_color'),
          feature: lenses.color,
        },
      ],
    }

    if (product.type != OPTICAL && !!lenses.treatment) {
      lensInformation.features.push({
        key: this.translate('treatment'),
        feature: lenses.treatment,
      })
    }

    if (lenses.hasDontDriveDisclaimer && wcs.isAustralia()) {
      lensInformation.features.push({
        key: '',
        feature: drivingMessage,
      })
    }

    return lensInformation
  }

  mapProductDimentions = (
    features: Features,
    size: Size | undefined,
    productIsSupernova: boolean
  ): ProductDetail => {
    const productDimensionIcon = productIsSupernova
      ? 'smart-glasses-size'
      : 'sunglasses-lens-height'
    const millimeters = '<span class="rb-product-detail__measure">mm</span>'
    return {
      key: this.translate('product_dimentions'),
      icon: productDimensionIcon,
      features: [
        {
          key: this.translation('pdp.size'),
          feature: features.frame.bridgeSize + millimeters,
        },
        {
          key: this.translate('lens_height'),
          feature: features.lenses.height + millimeters,
        },
        {
          key: this.translate('temple_length'),
          feature: features.frame.templeLength + millimeters,
        },
      ],
      sizePanel: {
        label: '',
      },
    }
  }

  mapIncludedInYourOrder = (product: Product): IncludedInYourOrder[] => {
    const includedInYourOrder = []
    const productIsSupernova = product.isSupernova

    if (![CASE, ACCESSORIES, ...ALL_ELECTRONICS_PRODUCT_TYPES].includes(product.type)) {
      includedInYourOrder.push({ icon: 'sunglasses-case', text: 'accessories_case' })
    }

    if (isProductStellaByProductType(product.type)) {
      const chargingCaseicon = productIsSupernova ? 'charging-case' : 'stella-case'
      includedInYourOrder.push({ icon: chargingCaseicon, text: 'charging_case' })
      if (!productIsSupernova) {
        includedInYourOrder.push({ icon: 'stella-cable', text: 'accessories_cable' })
      }
    }

    if (![CASE, ACCESSORIES].includes(product.type)) {
      includedInYourOrder.push({ icon: 'sunglasses-tissue', text: 'cleaning_cloth' })
    }

    if (productIsSupernova) {
      includedInYourOrder.push({ icon: 'supernova-guide', text: 'quick_start_guide' })
    }

    return includedInYourOrder
  }
}

export default new ProductDetails()
